


import { Card, allCards } from './data/cards';
import { triggerAbilities, AbilityResult, CardInstance } from './abilityEngine';
import { getCurrentHero, getHumanPlayer } from './gameState';
import { applyPreCombatHeroPassives, applyPreCombatHeroPassivesWithEnemyContext } from './heroPowers';
import { applyFactionAuras, TribeCombatHooks } from './synergyAuras';
import type { CardInstance as SynergyCardInstance } from './synergyHelpers';
import { makeMorrito } from './data/cards/tokens';
import { resolvePermanentBuff, buffApplications } from './buffs';

// Define types for combat events and results for clarity
export type CombatEvent =
  | { type: 'attack'; attackerId: string; defenderId: string; damage: number; counterDamage?: number }
  | { type: 'death'; unitId: string; owner?: 'player' | 'enemy' }
  | { type: 'hero_effect'; effectId: string; details: any }
  | { type: 'ability_buff'; targetId: string; atk: number; hp: number }
  | { type: 'ability_summon'; card: Card; position: number; owner: 'player' | 'enemy' }
  | { type: 'hero_heal'; amount: number; source: string }
  | { type: 'permanent_buff'; unitId: string; atk: number; hp: number; source: string }
  | { type: 'divine_shield_pop'; unitId: string }
  | { type: 'poisonous_kill'; attackerId: string; targetId: string }
  | { type: 'reborn'; unitId: string; owner: 'player' | 'enemy' }
  | { type: 'cleave_damage'; attackerId: string; targetId: string; damage: number }
  | { type: 'overkill_spill'; attackerId: string; targetId: string; damage: number }
  | { type: 'avenge_trigger'; unitId: string; abilityId: string };

export interface CombatResult {
  winner: 'player' | 'enemy' | 'draw';
  damage: number;
  events: CombatEvent[];
  playerUnitsThatDealtDamage?: Set<string>; // Instance IDs of player units that dealt damage (for Old School Writer)
}

/**
 * Guard (a.k.a. Taunt) — must be attacked before anything behind it.
 *
 * The card data uses both spellings: the two Guardian units carry the
 * `guard` tag, while Guardián Inmortal carries `taunt`. Both targeting
 * paths (the normal attack and the Windfury second swing) go through
 * this predicate so neither keyword is silently ignored.
 */
function isGuardUnit(unit: Card): boolean {
  return !!unit.tags && (unit.tags.includes('guard') || unit.tags.includes('taunt'));
}

/**
 * Simulates a combat encounter between two boards of units.
 * @param playerBoard - An array of Card objects for the player.
 * @param enemyBoard - An array of Card objects for the enemy.
 * @param preCombatEvents - An array of events that occurred before combat began.
 * @param opts.heroPassives - Whether to apply the human hero's pre-combat
 *   passives to the player side. True for the human's own fight; MUST be
 *   false for AI-vs-AI and server-side fights, where "the player" is not
 *   the human and the v1 getCurrentHero() singleton would leak the human's
 *   hero power into someone else's combat.
 * @returns A CombatResult object with the outcome and event log.
 */
export function simulateCombat(
  playerBoard: (Card | null)[],
  enemyBoard: Card[],
  preCombatEvents: CombatEvent[] = [],
  opts: { heroPassives?: boolean } = {},
): CombatResult {
  const heroPassives = opts.heroPassives !== false;
  // Create deep copies of the boards to avoid modifying the original game state.
  // Use 'let' as the arrays will be reassigned when units die.
  let simPlayerBoard: Card[] = JSON.parse(JSON.stringify(playerBoard.filter(Boolean)));
  let simEnemyBoard: Card[] = JSON.parse(JSON.stringify(enemyBoard));

  // Fold tavern-granted "this combat only" buffs into the sim copies.
  // The real boards keep the combatBuff field; the round commit clears it.
  const foldCombatBuff = (unit: Card) => {
    if (unit.combatBuff) {
      unit.attack += unit.combatBuff.attack;
      unit.health += unit.combatBuff.health;
      delete unit.combatBuff;
    }
  };
  simPlayerBoard.forEach(foldCombatBuff);
  simEnemyBoard.forEach(foldCombatBuff);

  // Tavern-granted debuffs aimed at the OTHER board (El Surfer). Same
  // lifecycle as combatBuff: set on summon, spent here, cleared on commit.
  // Health never drops below 1 — a debuff should weaken a board, not wipe it.
  const foldEnemyDebuff = (source: Card[], victims: Card[]) => {
    for (const unit of source) {
      const debuff = unit.enemyDebuff;
      if (!debuff) continue;
      for (const victim of victims) {
        victim.attack = Math.max(0, victim.attack + debuff.attack);
        victim.health = Math.max(1, victim.health + debuff.health);
      }
      delete unit.enemyDebuff;
    }
  };
  foldEnemyDebuff(simPlayerBoard, simEnemyBoard);
  foldEnemyDebuff(simEnemyBoard, simPlayerBoard);

  const events: CombatEvent[] = [...preCombatEvents];

  if (heroPassives) {
    // Board-only hero passives (frontline health, first strike, stat swap,
    // start-of-combat attack buff) ...
    events.push(...applyPreCombatHeroPassives(simPlayerBoard));
    // ... and hero powers that need enemy board context (e.g., Neighborhood
    // Watcher's scout, El del Slam's opening hit).
    events.push(...applyPreCombatHeroPassivesWithEnemyContext(simPlayerBoard, simEnemyBoard));
  }

  /**
   * Writes a stat change onto a sim board, routing permanent buffs through
   * the amplifier/watcher resolver so cards like Coleccionista de Metal
   * ("permanent buffs on this unit are doubled") and Muralista ("whenever a
   * friendly gains permanent stats, gain +1 Attack") work in combat too.
   *
   * `combat_buff` results skip the resolver: those are explicitly
   * this-combat-only, and the amplifier cards all say "permanent buffs".
   */
  const applyBuffResult = (
    board: Card[],
    result: { type: 'buff' | 'combat_buff'; targetIndex: number; atkDelta: number; hpDelta: number },
  ) => {
    const write = (idx: number, atk: number, hp: number) => {
      const target = board[idx];
      if (!target) return;
      target.attack += atk;
      target.health += hp;
      events.push({ type: 'ability_buff', targetId: target.instanceId!, atk, hp });
    };

    if (result.type === 'combat_buff') {
      write(result.targetIndex, result.atkDelta, result.hpDelta);
      return;
    }
    const resolved = resolvePermanentBuff(board, result.targetIndex, result.atkDelta, result.hpDelta);
    for (const app of buffApplications(resolved)) {
      write(app.targetIndex, app.atk, app.hp);
    }
  };

  /** Resolves start_of_combat for one side, with the other side as context. */
  const fireStartOfCombat = (own: Card[], foe: Card[], owner: 'player' | 'enemy') => {
    own.forEach((unit, index) => {
      const results = triggerAbilities('start_of_combat', index, own, {
        owner,
        enemyBoard: foe as CardInstance[],
      });
      results.forEach(result => {
        if (result.type === 'buff' || result.type === 'combat_buff') {
          applyBuffResult(own, result);
        } else if (result.type === 'swap_positions') {
          // Bailarín de Respaldo trades slots before anyone swings.
          const { a, b } = result;
          if (own[a] && own[b]) {
            [own[a], own[b]] = [own[b], own[a]];
          }
        }
      });
    });
  };
  fireStartOfCombat(simPlayerBoard, simEnemyBoard, 'player');
  fireStartOfCombat(simEnemyBoard, simPlayerBoard, 'enemy');

  // --- Tribe synergies: the six power-fantasy verbs ---
  // Deliberately resolved AFTER start_of_combat card abilities so the tribe
  // payoff amplifies whatever the cards just built: La Tocada doubles the
  // post-buff Attack, and the Bajitos hop copies the post-buff Health.
  const playerAura = applyFactionAuras(simPlayerBoard as SynergyCardInstance[]);
  const enemyAura = applyFactionAuras(simEnemyBoard as SynergyCardInstance[]);
  events.push(...playerAura.events, ...enemyAura.events);
  const playerHooks: TribeCombatHooks = playerAura.hooks;
  const enemyHooks: TribeCombatHooks = enemyAura.hooks;

  /**
   * Health each unit entered the fight with, after every start-of-combat
   * effect settled. Anclas 6 reads this to bring Reborn units back at full
   * strength instead of at 1 HP.
   */
  const fullHealthAtStart = new Map<string, number>();
  const recordFullHealth = (unit: Card) => {
    if (unit.instanceId) fullHealthAtStart.set(unit.instanceId, unit.health);
  };
  simPlayerBoard.forEach(recordFullHealth);
  simEnemyBoard.forEach(recordFullHealth);

  // Track player units that dealt damage (for Old School Writer hero power)
  const playerDamageDealers = new Set<string>();

  // Track units that have taken damage this combat (for "first time damaged" abilities)
  const damagedUnits = new Set<string>();

  // Track friendly deaths for avenge mechanic
  let playerFriendlyDeaths = 0;
  let enemyFriendlyDeaths = 0;

  // --- Determine who attacks first based on "firstStrike" flag ---
  const playerFirstUnit = simPlayerBoard.length > 0 ? simPlayerBoard[0] : null;
  const enemyFirstUnit = simEnemyBoard.length > 0 ? simEnemyBoard[0] : null;

  const playerHasFirstStrike = !!(playerFirstUnit && playerFirstUnit.flags && playerFirstUnit.flags.firstStrike);
  const enemyHasFirstStrike = !!(enemyFirstUnit && enemyFirstUnit.flags && enemyFirstUnit.flags.firstStrike);

  let playerGoesFirst = true; // Default
  if (playerHasFirstStrike && !enemyHasFirstStrike) {
      playerGoesFirst = true;
  } else if (!playerHasFirstStrike && enemyHasFirstStrike) {
      playerGoesFirst = false;
  }
  // If both or neither have first strike, the default (playerGoesFirst = true) is used.

  let turn: 'player' | 'enemy' = playerGoesFirst ? 'player' : 'enemy';
  let combatRound = 0;
  const MAX_ROUNDS = 50; // Safety break to prevent infinite loops

  /** Whether either side has swung yet — "the first unit to attack". */
  const openingSwingTaken = { player: false, enemy: false };

  // Track units that have dealt damage (for Old School Writer hero power)
  const damageDealtThisCombat = new Set<string>();
  const hero = getCurrentHero();

  /**
   * Damage shields — flat reductions that are NOT Divine Shield.
   *
   * Two sources, both resolved once here at combat start rather than looked
   * up on every hit: a unit reducing damage to itself (Aprendiz de Sonido)
   * and a unit reducing damage to whoever stands behind it (Escudo del
   * Barrio). Keyed by instanceId; `firstHitOnly` entries burn on use.
   */
  const damageShields = new Map<string, { amount: number; firstHitOnly: boolean; used: boolean }>();
  const registerShield = (unit: Card | undefined, amount: number, firstHitOnly: boolean) => {
    if (!unit?.instanceId || amount <= 0) return;
    const existing = damageShields.get(unit.instanceId);
    // Stacking shields add up; the strictest firstHitOnly wins.
    if (existing) {
      existing.amount += amount;
      existing.firstHitOnly = existing.firstHitOnly && firstHitOnly;
    } else {
      damageShields.set(unit.instanceId, { amount, firstHitOnly, used: false });
    }
  };
  const collectShields = (b: Card[]) => {
    b.forEach((unit, i) => {
      for (const a of unit.abilities ?? []) {
        if (a.id === 'reduce_damage_taken') {
          registerShield(unit, a.params?.amount ?? 0, a.params?.firstHitOnly !== false);
        } else if (a.id === 'reduce_damage_behind') {
          // "The unit behind this" — the next slot toward the back.
          registerShield(b[i + 1], a.params?.amount ?? 0, a.params?.firstHitOnly !== false);
        }
      }
    });
  };
  collectShields(simPlayerBoard);
  collectShields(simEnemyBoard);

  // Helper function to apply damage with Divine Shield check
  const applyDamage = (target: Card, damage: number, attacker: Card | null): { actualDamage: number; shieldPopped: boolean; poisonKill: boolean } => {
    let actualDamage = damage;
    let shieldPopped = false;
    let poisonKill = false;

    // Flat reduction runs before Divine Shield: chip damage absorbed down
    // to 0 by a shield never pops the bubble.
    const shield = target.instanceId ? damageShields.get(target.instanceId) : undefined;
    if (shield && damage > 0 && !(shield.firstHitOnly && shield.used)) {
      damage = Math.max(0, damage - shield.amount);
      actualDamage = damage;
      shield.used = true;
      if (damage === 0) return { actualDamage: 0, shieldPopped: false, poisonKill: false };
    }

    // Check Divine Shield
    if (target.flags?.divineShield && damage > 0) {
      target.flags.divineShield = false;
      shieldPopped = true;
      actualDamage = 0;
    } else {
      target.health -= damage;
      // Check if attacker has poisonous and dealt damage
      if (attacker?.flags?.poisonous && damage > 0 && target.health > 0) {
        target.health = 0;
        poisonKill = true;
      }
    }

    return { actualDamage, shieldPopped, poisonKill };
  };

  /**
   * Slameros 6 — Overkill. When a hit kills with damage to spare, the excess
   * rolls onto the next enemy in line, and keeps rolling while damage remains.
   * A 30-Attack Slamero can clear a board in one swing.
   *
   * @param excess Damage left over after the original target hit 0 or below.
   */
  const spillOverkill = (
    attacker: Card,
    board: Card[],
    fromIndex: number,
    excess: number,
  ): void => {
    let remaining = excess;
    for (let i = fromIndex + 1; i < board.length && remaining > 0; i++) {
      const target = board[i];
      if (!target || target.health <= 0) continue;

      const result = applyDamage(target, remaining, attacker);
      events.push({
        type: 'overkill_spill',
        attackerId: attacker.instanceId!,
        targetId: target.instanceId!,
        damage: remaining,
      });
      if (result.shieldPopped) {
        events.push({ type: 'divine_shield_pop', unitId: target.instanceId! });
        remaining = 0; // the shield eats the whole spill
      } else {
        if (result.poisonKill) {
          events.push({ type: 'poisonous_kill', attackerId: attacker.instanceId!, targetId: target.instanceId! });
        }
        remaining = target.health < 0 ? -target.health : 0;
      }
    }
  };

  /**
   * Resolves one attack's damage on the primary target, including the
   * Overkill spill. Returns the same shape applyDamage does so callers can
   * keep their existing shield/poison/on-damage handling.
   */
  const strike = (
    attacker: Card,
    defenderBoard: Card[],
    defenderIndex: number,
    damage: number,
  ) => {
    const defender = defenderBoard[defenderIndex];
    const result = applyDamage(defender, damage, attacker);
    if (attacker.flags?.overkill && !result.shieldPopped && defender.health < 0) {
      spillOverkill(attacker, defenderBoard, defenderIndex, -defender.health);
    }
    return result;
  };

  // Helper function to check and trigger avenge abilities
  const checkAvengeAbilities = (board: Card[], owner: 'player' | 'enemy', deathCount: number) => {
    for (let i = 0; i < board.length; i++) {
      const unit = board[i];
      if (!unit || unit.health <= 0) continue;

      // Initialize avenge counter if not set
      if (unit.avengeCounter === undefined) unit.avengeCounter = 0;
      unit.avengeCounter += deathCount;

      // Check for avenge abilities
      if (unit.abilities) {
        for (const ability of unit.abilities) {
          if (ability.trigger === 'avenge') {
            const threshold = ability.params?.threshold || 1;
            while (unit.avengeCounter >= threshold) {
              unit.avengeCounter -= threshold;
              events.push({ type: 'avenge_trigger', unitId: unit.instanceId!, abilityId: ability.id });

              // Apply avenge effect
              const results = triggerAbilities('avenge', i, board as CardInstance[], { owner });
              results.forEach(result => {
                if (result.type === 'buff' || result.type === 'combat_buff') {
                  applyBuffResult(board, result);
                }
              });
            }
          }
        }
      }
    }
  };

    // --- Process Deaths and Last Breaths ---
    const processDeathsAndLastBreaths = (playerB: Card[], enemyB: Card[]): { pBoard: Card[], eBoard: Card[], events: CombatEvent[] } => {
        const deadUnits: { unit: Card; index: number; owner: 'player' | 'enemy'; board: Card[] }[] = [];
        
        playerB.forEach((unit, index) => {
            if (unit.health <= 0) deadUnits.push({ unit, index, owner: 'player', board: playerB });
        });
        enemyB.forEach((unit, index) => {
            if (unit.health <= 0) deadUnits.push({ unit, index, owner: 'enemy', board: enemyB });
        });

        if (deadUnits.length === 0) {
            return { pBoard: playerB, eBoard: enemyB, events: [] };
        }

        const newEvents: CombatEvent[] = [];
        const abilityResults: { result: AbilityResult; deadUnitPosition: number; owner: 'player' | 'enemy' }[] = [];
        const rebornUnits: { unit: Card; index: number; owner: 'player' | 'enemy' }[] = [];

        // Count deaths for avenge
        const playerDeathCount = deadUnits.filter(d => d.owner === 'player').length;
        const enemyDeathCount = deadUnits.filter(d => d.owner === 'enemy').length;
        playerFriendlyDeaths += playerDeathCount;
        enemyFriendlyDeaths += enemyDeathCount;

        // 1. Trigger lastbreaths and gather results/death events
        for (const { unit, index, owner, board } of deadUnits) {
            newEvents.push({ type: 'death', unitId: unit.instanceId!, owner });

            // Check for Reborn
            if (unit.flags?.reborn && !unit.hasReborned) {
                rebornUnits.push({ unit, index, owner });
            }

            const results = triggerAbilities('lastbreath', index, board as CardInstance[], { owner });
            results.forEach(result => {
                abilityResults.push({ result, deadUnitPosition: index, owner });
            });
        }

        // 2. Check for Scene Guardian hero power (heal when friendly unit dies)
        const hero = getCurrentHero();
        if (hero?.powerType === 'passive_on_unit_death') {
            const friendlyDeaths = deadUnits.filter(d => d.owner === 'player').length;
            if (friendlyDeaths > 0) {
                const healAmount = hero.powerConfig.amount || 1;
                const totalHeal = healAmount * friendlyDeaths;
                newEvents.push({
                    type: 'hero_heal',
                    amount: totalHeal,
                    source: 'scene_guardian'
                });
            }
        }

        // 3. Create survivor boards
        // 1.5. Trigger on_friendly_death for surviving units on both boards
        playerB.forEach((unit, index) => {
            if (unit.health > 0) {
                const playerDeathCount = deadUnits.filter(d => d.owner === 'player').length;
                if (playerDeathCount > 0) {
                    const results = triggerAbilities('on_friendly_death', index, playerB as CardInstance[], { owner: 'player' });
                    results.forEach(result => {
                        abilityResults.push({ result, deadUnitPosition: -1, owner: 'player' });
                    });
                }
            }
        });
        enemyB.forEach((unit, index) => {
            if (unit.health > 0) {
                const enemyDeathCount = deadUnits.filter(d => d.owner === 'enemy').length;
                if (enemyDeathCount > 0) {
                    const results = triggerAbilities('on_friendly_death', index, enemyB as CardInstance[], { owner: 'enemy' });
                    results.forEach(result => {
                        abilityResults.push({ result, deadUnitPosition: -1, owner: 'enemy' });
                    });
                }
            }
        });

        // 2. Create survivor boards
        let nextPlayerBoard = playerB.filter(u => u.health > 0);
        let nextEnemyBoard = enemyB.filter(u => u.health > 0);

        // 2.5. Handle Reborn - resurrect units with 1 HP.
        // Anclas 6 (SEGUNDO AIRE) upgrades this: the unit comes back at the
        // Health it started the fight with, plus a bonus, so killing it is
        // strictly worse for the attacker than leaving it alone.
        for (const { unit, index, owner } of rebornUnits) {
            const targetBoard = owner === 'player' ? nextPlayerBoard : nextEnemyBoard;
            if (targetBoard.length < 7) {
                const upgrade = (owner === 'player' ? playerHooks : enemyHooks).rebornUpgrade;
                const baseHealth = upgrade?.fullHealth
                    ? (fullHealthAtStart.get(unit.instanceId!) ?? unit.health ?? 1)
                    : 1;
                const rebornUnit: Card = {
                    ...unit,
                    attack: unit.attack + (upgrade?.bonus.attack ?? 0),
                    health: Math.max(1, baseHealth) + (upgrade?.bonus.health ?? 0),
                    hasReborned: true,
                    instanceId: `${unit.id}_reborn_${crypto.randomUUID()}`,
                };
                recordFullHealth(rebornUnit);
                // Insert at the position where the unit died
                const unitsDeadBefore = deadUnits.filter(d => d.owner === owner && d.index < index).length;
                const insertPos = Math.min(index - unitsDeadBefore, targetBoard.length);
                targetBoard.splice(insertPos, 0, rebornUnit);
                newEvents.push({ type: 'reborn', unitId: rebornUnit.instanceId!, owner });
            }
        }

        // 2.55. La Clica (RAZA) — the block refills. Every qualifying death
        // sends out another Morrito, and at the 6-breakpoint each Morrito that
        // arrives buffs the whole swarm, so a long fight compounds: 5 deaths
        // means 5 new bodies AND +10/+10 spread across every token you own.
        for (const owner of ['player', 'enemy'] as const) {
            const hooks = owner === 'player' ? playerHooks : enemyHooks;
            if (hooks.deathSummons.length === 0) continue;

            const survivors = owner === 'player' ? nextPlayerBoard : nextEnemyBoard;
            const ownDeaths = deadUnits.filter(d => d.owner === owner);

            for (const hook of hooks.deathSummons) {
                for (const dead of ownDeaths) {
                    if (hook.scope === 'tribe' && dead.unit.tribe !== hook.tribe) continue;
                    if (survivors.length >= 7) break;

                    const morrito = makeMorrito(
                        hook.token.attack,
                        hook.token.health,
                        `${hook.tribe}_morrito_${crypto.randomUUID()}`,
                    );
                    const unitsDeadBefore = ownDeaths.filter(d => d.index < dead.index).length;
                    const insertPos = Math.min(
                        Math.max(0, dead.index - unitsDeadBefore),
                        survivors.length,
                    );
                    survivors.splice(insertPos, 0, morrito);
                    recordFullHealth(morrito);
                    newEvents.push({
                        type: 'ability_summon',
                        card: morrito,
                        position: insertPos,
                        owner,
                    });

                    // Every arrival makes the existing swarm bigger.
                    if (hook.swarmBuff) {
                        for (const unit of survivors) {
                            if (!unit.tags?.includes('token')) continue;
                            unit.attack += hook.swarmBuff.attack;
                            unit.health += hook.swarmBuff.health;
                            newEvents.push({
                                type: 'ability_buff',
                                targetId: unit.instanceId!,
                                atk: hook.swarmBuff.attack,
                                hp: hook.swarmBuff.health,
                            });
                        }
                    }
                }
            }
        }

        // 2.6. Check and trigger avenge abilities
        if (playerDeathCount > 0) {
            checkAvengeAbilities(nextPlayerBoard, 'player', playerDeathCount);
        }
        if (enemyDeathCount > 0) {
            checkAvengeAbilities(nextEnemyBoard, 'enemy', enemyDeathCount);
        }

        // 4. Apply ability results
        for (const { result, deadUnitPosition, owner } of abilityResults) {
            const friendlyBoardBeforeDeath = (owner === 'player' ? playerB : enemyB);
            const friendlyBoardAfterDeath = (owner === 'player' ? nextPlayerBoard : nextEnemyBoard);

            if (result.type === 'buff') {
                // For on_friendly_death buffs (deadUnitPosition === -1), target is already on the survivor board
                if (deadUnitPosition === -1) {
                    const targetOnNewBoard = friendlyBoardAfterDeath[result.targetIndex];
                    if (targetOnNewBoard) {
                        targetOnNewBoard.attack += result.atkDelta;
                        targetOnNewBoard.health += result.hpDelta;
                        newEvents.push({
                            type: 'ability_buff',
                            targetId: targetOnNewBoard.instanceId!,
                            atk: result.atkDelta,
                            hp: result.hpDelta,
                        });
                    }
                } else {
                    // For lastbreath buffs, we need to map from the old board to the new board
                    const targetOnOriginalBoard = friendlyBoardBeforeDeath[result.targetIndex];
                    if (targetOnOriginalBoard) {
                        const targetOnNewBoard = friendlyBoardAfterDeath.find(u => u.instanceId === targetOnOriginalBoard.instanceId);
                        if (targetOnNewBoard) {
                            targetOnNewBoard.attack += result.atkDelta;
                            targetOnNewBoard.health += result.hpDelta;
                            newEvents.push({
                                type: 'ability_buff',
                                targetId: targetOnNewBoard.instanceId!,
                                atk: result.atkDelta,
                                hp: result.hpDelta,
                            });
                        }
                    }
                }
            } else if (result.type === 'summon') {
                if (friendlyBoardAfterDeath.length < 7) {
                    const cardTemplate = allCards.find(c => c.id === result.cardId);
                    if (cardTemplate) {
                        const newCardInstance: Card = { ...cardTemplate, instanceId: `${result.cardId}_${crypto.randomUUID()}` };
                        
                        const unitsDeadBefore = deadUnits.filter(d => d.owner === owner && d.index < deadUnitPosition).length;
                        const insertPos = deadUnitPosition - unitsDeadBefore;

                        friendlyBoardAfterDeath.splice(insertPos, 0, newCardInstance);
                        newEvents.push({
                            type: 'ability_summon',
                            card: newCardInstance,
                            position: insertPos,
                            owner,
                        });
                    }
                }
            }
        }

        return { pBoard: nextPlayerBoard, eBoard: nextEnemyBoard, events: newEvents };
    };

  // --- Slameros (CODAZO) opening damage ---
  // Fires before the first swing, then deaths resolve immediately so a unit
  // killed by the opener never gets to attack.
  const fireOpeningDamage = (
    hooks: TribeCombatHooks,
    targetBoard: Card[],
    attackerLabel: string,
  ) => {
    for (const opener of hooks.openingDamage) {
      for (let i = 0; i < opener.count; i++) {
        const living = targetBoard.filter(u => u.health > 0);
        if (living.length === 0) return;
        const target = living[Math.floor(Math.random() * living.length)];
        const result = applyDamage(target, opener.damage, null);
        if (result.shieldPopped) {
          events.push({ type: 'divine_shield_pop', unitId: target.instanceId! });
        }
        events.push({
          type: 'hero_effect',
          effectId: 'tribe_opening_damage',
          details: {
            targetId: target.instanceId!,
            damage: opener.damage,
            tribeName: opener.tribeName,
            source: attackerLabel,
          },
        });
      }
    }
  };

  // Cards with their own opening barrage (Francotirador) ride the same path
  // as the Slameros breakpoint, so ordering and death cleanup stay identical.
  const collectCardOpeners = (board: Card[], hooks: TribeCombatHooks) => {
    for (const unit of board) {
      for (const ability of unit.abilities ?? []) {
        if (ability.id !== 'damage_random_enemies') continue;
        if (ability.trigger !== 'start_of_combat') continue;
        hooks.openingDamage.push({
          damage: ability.params?.damage ?? 0,
          count: ability.params?.count ?? 1,
          tribeName: unit.name,
        });
      }
    }
  };
  collectCardOpeners(simPlayerBoard, playerHooks);
  collectCardOpeners(simEnemyBoard, enemyHooks);

  fireOpeningDamage(playerHooks, simEnemyBoard, 'player');
  fireOpeningDamage(enemyHooks, simPlayerBoard, 'enemy');

  if (playerHooks.openingDamage.length > 0 || enemyHooks.openingDamage.length > 0) {
    const opening = processDeathsAndLastBreaths(simPlayerBoard, simEnemyBoard);
    simPlayerBoard = opening.pBoard;
    simEnemyBoard = opening.eBoard;
    events.push(...opening.events);
  }

  while (simPlayerBoard.length > 0 && simEnemyBoard.length > 0 && combatRound < MAX_ROUNDS) {
    let attacker: Card;
    let defender: Card;
    let attackerBoard: Card[];
    let defenderBoard: Card[];
    let attackerIsPlayer = false;

    if (turn === 'player') {
      attacker = simPlayerBoard[0];
      attackerBoard = simPlayerBoard;
      defenderBoard = simEnemyBoard;
      attackerIsPlayer = true;
      turn = 'enemy';
    } else { // turn === 'enemy'
      attacker = simEnemyBoard[0];
      attackerBoard = simEnemyBoard;
      defenderBoard = simPlayerBoard;
      attackerIsPlayer = false;
      turn = 'player';
    }

    // --- Cannot Attack check ---
    // Units with cannotAttack flag skip their attack turn
    if (attacker.flags?.cannotAttack) {
      combatRound++;
      continue;
    }

    // --- Guard targeting logic ---
    // Must attack units with Guard first if any exist
    const guardUnits = defenderBoard.filter(isGuardUnit);
    if (guardUnits.length > 0) {
      // Randomly select one of the guard units
      defender = guardUnits[Math.floor(Math.random() * guardUnits.length)];
    } else {
      // No guard units, attack the first unit
      defender = defenderBoard[0];
    }
    // --- End Guard targeting ---

    const defenderIndex = defenderBoard.indexOf(defender);

    // Check if attacker has first strike
    const attackerHasFirstStrike = !!(attacker.flags?.firstStrike);
    const defenderHasFirstStrike = !!(defender.flags?.firstStrike);

    // Simulate the attack with simultaneous damage (or first strike if applicable)
    const attackerDamage = attacker.attack;
    const counterDamage = defender.attack;

    // Apply damage to defender (Divine Shield / Poisonous / Overkill spill)
    const defenderResult = strike(attacker, defenderBoard, defenderIndex, attackerDamage);
    if (defenderResult.shieldPopped) {
      events.push({ type: 'divine_shield_pop', unitId: defender.instanceId! });
    }
    if (defenderResult.poisonKill) {
      events.push({ type: 'poisonous_kill', attackerId: attacker.instanceId!, targetId: defender.instanceId! });
    }

    // Apply counter damage to attacker (simultaneous combat)
    // UNLESS attacker has first strike and defender doesn't (and defender is now dead)
    const shouldApplyCounterDamage = !(attackerHasFirstStrike && !defenderHasFirstStrike && defender.health <= 0);

    let attackerResult = { actualDamage: 0, shieldPopped: false, poisonKill: false };
    if (shouldApplyCounterDamage) {
      attackerResult = applyDamage(attacker, counterDamage, defender);
      if (attackerResult.shieldPopped) {
        events.push({ type: 'divine_shield_pop', unitId: attacker.instanceId! });
      }
      if (attackerResult.poisonKill) {
        events.push({ type: 'poisonous_kill', attackerId: defender.instanceId!, targetId: attacker.instanceId! });
      }
    }

    events.push({
      type: 'attack',
      attackerId: attacker.instanceId!,
      defenderId: defender.instanceId!,
      damage: attackerDamage,
      counterDamage: counterDamage,
    });

    // --- Attack-driven triggers ---
    // Fired after the swing resolves so a unit that scales off connecting
    // (Aprendiz de Placazos) does not get its bonus counted into the hit
    // that earned it.
    {
      const attackerOwner: 'player' | 'enemy' = attackerIsPlayer ? 'player' : 'enemy';
      const attackerIdx = attackerBoard.indexOf(attacker);
      const isOpeningSwing = !openingSwingTaken[attackerOwner];
      openingSwingTaken[attackerOwner] = true;

      const runAttackTrigger = (trigger: 'on_deal_damage' | 'on_first_attack', sourceIdx: number) => {
        if (sourceIdx < 0) return;
        const res = triggerAbilities(trigger, sourceIdx, attackerBoard as CardInstance[], {
          owner: attackerOwner,
          damageAmount: defenderResult.actualDamage,
          enemyBoard: defenderBoard as CardInstance[],
          enemyTargetIndex: defenderIndex,
          attackerIndex: attackerIdx,
        });
        for (const r of res) {
          if (r.type === 'buff' || r.type === 'combat_buff') {
            applyBuffResult(attackerBoard, r);
          } else if (r.type === 'damage_enemy') {
            const splashTarget = defenderBoard[r.targetIndex];
            if (splashTarget && splashTarget.health > 0) {
              const splash = applyDamage(splashTarget, r.amount, attacker);
              events.push({
                type: 'cleave_damage',
                attackerId: attacker.instanceId!,
                targetId: splashTarget.instanceId!,
                damage: r.amount,
              });
              if (splash.shieldPopped) {
                events.push({ type: 'divine_shield_pop', unitId: splashTarget.instanceId! });
              }
            }
          }
        }
      };

      if (defenderResult.actualDamage > 0) {
        runAttackTrigger('on_deal_damage', attackerIdx);
      }

      // "The first unit to attack in a combat" — once per side. Fired on
      // EVERY slot, not just the attacker, because two different card
      // shapes hang off this moment: the opening attacker rewarding itself
      // (Veterano del Círculo, Botas de Casquillo) and a bystander
      // rewarding whoever swung (Fotógrafa del Flash). Handlers tell the
      // two apart via `attackerIndex` in the context.
      if (isOpeningSwing) {
        for (let i = 0; i < attackerBoard.length; i++) {
          runAttackTrigger('on_first_attack', i);
        }
      }
    }

    // --- Cleave damage to adjacent units ---
    if (attacker.flags?.cleave && attackerDamage > 0) {
      // Damage units adjacent to defender
      if (defenderIndex > 0 && defenderBoard[defenderIndex - 1]) {
        const leftTarget = defenderBoard[defenderIndex - 1];
        const cleaveResult = applyDamage(leftTarget, attackerDamage, attacker);
        events.push({ type: 'cleave_damage', attackerId: attacker.instanceId!, targetId: leftTarget.instanceId!, damage: attackerDamage });
        if (cleaveResult.shieldPopped) {
          events.push({ type: 'divine_shield_pop', unitId: leftTarget.instanceId! });
        }
        if (cleaveResult.poisonKill) {
          events.push({ type: 'poisonous_kill', attackerId: attacker.instanceId!, targetId: leftTarget.instanceId! });
        }
      }
      if (defenderIndex < defenderBoard.length - 1 && defenderBoard[defenderIndex + 1]) {
        const rightTarget = defenderBoard[defenderIndex + 1];
        const cleaveResult = applyDamage(rightTarget, attackerDamage, attacker);
        events.push({ type: 'cleave_damage', attackerId: attacker.instanceId!, targetId: rightTarget.instanceId!, damage: attackerDamage });
        if (cleaveResult.shieldPopped) {
          events.push({ type: 'divine_shield_pop', unitId: rightTarget.instanceId! });
        }
        if (cleaveResult.poisonKill) {
          events.push({ type: 'poisonous_kill', attackerId: attacker.instanceId!, targetId: rightTarget.instanceId! });
        }
      }
    }

    // Check for Old School Writer hero power (first damage dealt)
    if (hero?.powerType === 'passive_on_damage_dealt' && hero.powerConfig.firstTimeOnly) {
      const isPlayerAttacker = turn === 'enemy'; // Turn switches after determining attacker
      if (isPlayerAttacker && !damageDealtThisCombat.has(attacker.instanceId!)) {
        damageDealtThisCombat.add(attacker.instanceId!);

        // Apply permanent buff
        const attackBonus = hero.powerConfig.attack || 0;
        const healthBonus = hero.powerConfig.health || 0;

        // Apply to the simulated unit (for this combat)
        attacker.attack += attackBonus;
        attacker.health += healthBonus;

        // Record event to apply to real board later
        events.push({
          type: 'permanent_buff',
          unitId: attacker.instanceId!,
          atk: attackBonus,
          hp: healthBonus,
          source: 'old_school_writer'
        });
      }
    }

    // Track if a player unit dealt damage (for Old School Writer)
    if (attackerIsPlayer && defenderResult.actualDamage > 0) {
      playerDamageDealers.add(attacker.instanceId!);
    }

    // Trigger on_take_damage for the defender if damage was dealt (not blocked by shield)
    if (defenderResult.actualDamage > 0) {
      const defenderOwner = attackerIsPlayer ? 'enemy' : 'player';
      const isFirstTimeDamaged = !damagedUnits.has(defender.instanceId!);

      // Mark this unit as damaged and increment hit counter
      damagedUnits.add(defender.instanceId!);
      defender.timesHitThisCombat = (defender.timesHitThisCombat || 0) + 1;

      const damageResults = triggerAbilities(
        'on_take_damage',
        defenderIndex,
        defenderBoard as CardInstance[],
        { owner: defenderOwner, damageAmount: defenderResult.actualDamage, isFirstTime: isFirstTimeDamaged }
      );

      // Apply on_take_damage ability results
      damageResults.forEach(result => {
        if (result.type === 'buff' || result.type === 'combat_buff') {
          applyBuffResult(defenderBoard, result);
        }
      });
    }

    // Trigger on_take_damage for the attacker (counter damage)
    if (attackerResult.actualDamage > 0) {
      const attackerOwner = attackerIsPlayer ? 'player' : 'enemy';
      const attackerIndex = attackerBoard.indexOf(attacker);
      const isFirstTimeDamaged = !damagedUnits.has(attacker.instanceId!);

      damagedUnits.add(attacker.instanceId!);
      attacker.timesHitThisCombat = (attacker.timesHitThisCombat || 0) + 1;

      const damageResults = triggerAbilities(
        'on_take_damage',
        attackerIndex,
        attackerBoard as CardInstance[],
        { owner: attackerOwner, damageAmount: attackerResult.actualDamage, isFirstTime: isFirstTimeDamaged }
      );

      damageResults.forEach(result => {
        if (result.type === 'buff' || result.type === 'combat_buff') {
          applyBuffResult(attackerBoard, result);
        }
      });
    }
    

    const { pBoard, eBoard, events: lastbreathEvents } = processDeathsAndLastBreaths(simPlayerBoard, simEnemyBoard);
    simPlayerBoard = pBoard;
    simEnemyBoard = eBoard;
    events.push(...lastbreathEvents);

    // --- Windfury: Second attack if attacker is alive and has windfury ---
    if (attacker.flags?.windfury && attacker.health > 0 && defenderBoard.length > 0) {
      // Find a new target for the second attack
      const newDefenderBoard = attackerIsPlayer ? simEnemyBoard : simPlayerBoard;
      if (newDefenderBoard.length > 0) {
        const tauntUnits2 = newDefenderBoard.filter(isGuardUnit);
        let defender2: Card;
        if (tauntUnits2.length > 0) {
          defender2 = tauntUnits2[Math.floor(Math.random() * tauntUnits2.length)];
        } else {
          defender2 = newDefenderBoard[Math.floor(Math.random() * newDefenderBoard.length)];
        }

        const defender2Index = newDefenderBoard.indexOf(defender2);
        const windfuryDamage = attacker.attack;
        const windfuryCounter = defender2.attack;

        // Check first strike for windfury attack too
        const attackerHasFirstStrike2 = !!(attacker.flags?.firstStrike);
        const defender2HasFirstStrike = !!(defender2.flags?.firstStrike);

        // Apply windfury attack damage (Overkill spills on this swing too)
        const def2Result = strike(attacker, newDefenderBoard, defender2Index, windfuryDamage);
        if (def2Result.shieldPopped) {
          events.push({ type: 'divine_shield_pop', unitId: defender2.instanceId! });
        }
        if (def2Result.poisonKill) {
          events.push({ type: 'poisonous_kill', attackerId: attacker.instanceId!, targetId: defender2.instanceId! });
        }

        // Apply counter damage (respecting first strike)
        const shouldApplyCounterDamage2 = !(attackerHasFirstStrike2 && !defender2HasFirstStrike && defender2.health <= 0);

        if (shouldApplyCounterDamage2) {
          const atk2Result = applyDamage(attacker, windfuryCounter, defender2);
          if (atk2Result.shieldPopped) {
            events.push({ type: 'divine_shield_pop', unitId: attacker.instanceId! });
          }
          if (atk2Result.poisonKill) {
            events.push({ type: 'poisonous_kill', attackerId: defender2.instanceId!, targetId: attacker.instanceId! });
          }
        }

        events.push({
          type: 'attack',
          attackerId: attacker.instanceId!,
          defenderId: defender2.instanceId!,
          damage: windfuryDamage,
          counterDamage: windfuryCounter,
        });

        // Cleave on second attack
        if (attacker.flags?.cleave && windfuryDamage > 0) {
          if (defender2Index > 0 && newDefenderBoard[defender2Index - 1]) {
            const leftTarget = newDefenderBoard[defender2Index - 1];
            const cleaveResult = applyDamage(leftTarget, windfuryDamage, attacker);
            events.push({ type: 'cleave_damage', attackerId: attacker.instanceId!, targetId: leftTarget.instanceId!, damage: windfuryDamage });
            if (cleaveResult.shieldPopped) events.push({ type: 'divine_shield_pop', unitId: leftTarget.instanceId! });
            if (cleaveResult.poisonKill) events.push({ type: 'poisonous_kill', attackerId: attacker.instanceId!, targetId: leftTarget.instanceId! });
          }
          if (defender2Index < newDefenderBoard.length - 1 && newDefenderBoard[defender2Index + 1]) {
            const rightTarget = newDefenderBoard[defender2Index + 1];
            const cleaveResult = applyDamage(rightTarget, windfuryDamage, attacker);
            events.push({ type: 'cleave_damage', attackerId: attacker.instanceId!, targetId: rightTarget.instanceId!, damage: windfuryDamage });
            if (cleaveResult.shieldPopped) events.push({ type: 'divine_shield_pop', unitId: rightTarget.instanceId! });
            if (cleaveResult.poisonKill) events.push({ type: 'poisonous_kill', attackerId: attacker.instanceId!, targetId: rightTarget.instanceId! });
          }
        }

        // Process deaths from windfury attack
        const { pBoard: pBoard2, eBoard: eBoard2, events: events2 } = processDeathsAndLastBreaths(simPlayerBoard, simEnemyBoard);
        simPlayerBoard = pBoard2;
        simEnemyBoard = eBoard2;
        events.push(...events2);
      }
    }

    combatRound++;
  }

  // Determine the winner and calculate damage
  let winner: 'player' | 'enemy' | 'draw';
  let damage = 0;

  const playerAlive = simPlayerBoard.length > 0;
  const enemyAlive = simEnemyBoard.length > 0;

  if (playerAlive && !enemyAlive) {
    winner = 'player';
    // Damage is the sum of tiers of surviving player units
    damage = simPlayerBoard.reduce((total, unit) => total + unit.tier, 0);
  } else if (!playerAlive && enemyAlive) {
    winner = 'enemy';
    // Damage is the sum of tiers of surviving enemy units
    damage = simEnemyBoard.reduce((total, unit) => total + unit.tier, 0);
  } else {
    // This case covers both sides having units (if MAX_ROUNDS hit) or both being wiped out simultaneously.
    winner = 'draw';
    damage = 0;
  }

  // --- Trigger after_combat_survives for surviving units ---
  // This triggers abilities like buff_self_after_combat and heal_self_after_combat
  for (let i = 0; i < simPlayerBoard.length; i++) {
    const results = triggerAbilities('after_combat_survives', i, simPlayerBoard, { owner: 'player' });
    for (const result of results) {
      if (result.type === 'buff' || result.type === 'combat_buff') {
        applyBuffResult(simPlayerBoard, result);
      }
    }
  }
  for (let i = 0; i < simEnemyBoard.length; i++) {
    const results = triggerAbilities('after_combat_survives', i, simEnemyBoard, { owner: 'enemy' });
    for (const result of results) {
      if (result.type === 'buff' || result.type === 'combat_buff') {
        applyBuffResult(simEnemyBoard, result);
      }
    }
  }
  // --- End after_combat_survives ---

  return { winner, damage, events, playerUnitsThatDealtDamage: playerDamageDealers };
}
